import React, { useState, useRef } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Bold, Italic, Underline, List, ListOrdered, Image, Link, Video, X, Upload, AlignLeft, AlignCenter, AlignRight, Star } from 'lucide-react';
interface VideoItem {
  id: string;
  title: string;
  thumbnailUrl: string;
  duration: string;
}
interface ArticleEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (article: any) => void;
  categories: string[];
  videos: VideoItem[];
}
export function ArticleEditorModal({
  isOpen,
  onClose,
  onSave,
  categories,
  videos
}: ArticleEditorModalProps) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(categories.filter(c => c !== 'All')[0] || 'Onboarding');
  const [attachedVideoId, setAttachedVideoId] = useState<string | null>(null);
  const [showVideoSelector, setShowVideoSelector] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [isRequired, setIsRequired] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const availableCategories = categories.filter(c => c !== 'All');
  const attachedVideo = videos.find(v => v.id === attachedVideoId);
  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };
  const handleAddImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      setImages([...images, url]);
      execCommand('insertHTML', `<img src="${url}" alt="Article image" class="max-w-full h-auto rounded-lg my-4" />`);
    }
  };
  const handleAddLink = () => {
    const url = prompt('Enter link URL:');
    if (url) execCommand('createLink', url);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const content = editorRef.current?.innerHTML || '';
    const plainText = editorRef.current?.innerText || '';
    onSave({
      id: Math.random().toString(36).substr(2, 9),
      title,
      content,
      excerpt: plainText.substring(0, 150) + (plainText.length > 150 ? '...' : ''),
      category,
      attachedVideoId,
      images,
      dateAdded: new Date().toISOString(),
      readTime: Math.max(1, Math.ceil(plainText.split(' ').length / 200)) + ' min read',
      isRequired
    });
    setTitle('');
    setCategory(availableCategories[0] || 'Onboarding');
    setAttachedVideoId(null);
    setImages([]);
    setIsRequired(false);
    if (editorRef.current) editorRef.current.innerHTML = '';
    onClose();
  };
  const ToolbarButton = ({
    onClick,
    active,
    children,
    title
  }: {
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
    title: string;
  }) => <button type="button" onClick={onClick} title={title} className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${active ? 'bg-gray-200 text-orange-600' : 'text-gray-600'}`} data-id="element-7">
      {children}
    </button>;
  return <Modal isOpen={isOpen} onClose={onClose} title="Create New Article" maxWidth="4xl" data-id="element-8">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-9">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10">
          <div data-id="element-11">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12">
              Article Title
            </label>
            <input type="text" required value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder="e.g., How to Submit Expense Reports" data-id="element-13" />
          </div>
          <div data-id="element-14">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15">
              Category
            </label>
            <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" data-id="element-16">
              {availableCategories.map(cat => <option key={cat} value={cat} data-id="element-17">
                  {cat}
                </option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-18">
          <div className="flex items-center h-5" data-id="element-19">
            <input id="isRequired" type="checkbox" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded" data-id="element-20" />
          </div>
          <div className="flex-1" data-id="element-21">
            <label htmlFor="isRequired" className="font-medium text-gray-700 flex items-center gap-2" data-id="element-22">
              Mark as Required
              <Star className="w-3.5 h-3.5 text-orange-500 fill-orange-500" data-id="element-23" />
            </label>
            <p className="text-xs text-gray-500" data-id="element-24">
              Employees will be required to read this article to complete their
              training.
            </p>
          </div>
        </div>
        <div data-id="element-25">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-26">
            Article Content
          </label>
          <div className="flex flex-wrap items-center gap-1 p-2 border border-gray-300 border-b-0 rounded-t-md bg-gray-50" data-id="element-27">
            <ToolbarButton onClick={() => execCommand('formatBlock', 'h1')} title="Heading 1" data-id="element-28">
              <div className="w-4 h-4" data-id="element-29" />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('formatBlock', 'h2')} title="Heading 2" data-id="element-30">
              <div className="w-4 h-4" data-id="element-31" />
            </ToolbarButton>
            <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-32" />
            <ToolbarButton onClick={() => execCommand('bold')} title="Bold" data-id="element-33">
              <Bold className="w-4 h-4" data-id="element-34" />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('italic')} title="Italic" data-id="element-35">
              <Italic className="w-4 h-4" data-id="element-36" />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('underline')} title="Underline" data-id="element-37">
              <Underline className="w-4 h-4" data-id="element-38" />
            </ToolbarButton>
            <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-39" />
            <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Align Left" data-id="element-40">
              <AlignLeft className="w-4 h-4" data-id="element-41" />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Align Center" data-id="element-42">
              <AlignCenter className="w-4 h-4" data-id="element-43" />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('justifyRight')} title="Align Right" data-id="element-44">
              <AlignRight className="w-4 h-4" data-id="element-45" />
            </ToolbarButton>
            <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-46" />
            <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Bullet List" data-id="element-47">
              <List className="w-4 h-4" data-id="element-48" />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numbered List" data-id="element-49">
              <ListOrdered className="w-4 h-4" data-id="element-50" />
            </ToolbarButton>
            <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-51" />
            <ToolbarButton onClick={handleAddImage} title="Insert Image" data-id="element-52">
              <Image className="w-4 h-4" data-id="element-53" />
            </ToolbarButton>
            <ToolbarButton onClick={handleAddLink} title="Insert Link" data-id="element-54">
              <Link className="w-4 h-4" data-id="element-55" />
            </ToolbarButton>
          </div>
          <div ref={editorRef} contentEditable className="w-full min-h-[200px] max-h-[300px] overflow-y-auto px-4 py-3 border border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent prose prose-sm max-w-none" style={{
          lineHeight: '1.6'
        }} onPaste={e => {
          e.preventDefault();
          document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
        }} data-id="element-56" />
        </div>
        <div data-id="element-57">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-58">
            Screenshots / Images
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-59">
            {images.map((img, idx) => <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200" data-id="element-60">
                <img src={img} alt="" className="w-full h-full object-cover" data-id="element-61" />
                <button type="button" onClick={() => setImages(images.filter((_, i) => i !== idx))} className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600" data-id="element-62">
                  <X className="w-3 h-3" data-id="element-63" />
                </button>
              </div>)}
            <button type="button" onClick={handleAddImage} className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-500 transition-colors" data-id="element-64">
              <Upload className="w-5 h-5" data-id="element-65" />
              <span className="text-xs mt-1" data-id="element-66">Add</span>
            </button>
          </div>
        </div>
        <div data-id="element-67">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-68">
            Attach Video (Optional)
          </label>
          {attachedVideo ? <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-69">
              <img src={attachedVideo.thumbnailUrl} alt="" className="w-16 h-10 object-cover rounded" data-id="element-70" />
              <div className="flex-1" data-id="element-71">
                <p className="text-sm font-medium text-gray-900" data-id="element-72">
                  {attachedVideo.title}
                </p>
                <p className="text-xs text-gray-500" data-id="element-73">
                  {attachedVideo.duration}
                </p>
              </div>
              <button type="button" onClick={() => setAttachedVideoId(null)} className="p-1 text-gray-400 hover:text-red-500" data-id="element-74">
                <X className="w-4 h-4" data-id="element-75" />
              </button>
            </div> : <button type="button" onClick={() => setShowVideoSelector(!showVideoSelector)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-76">
              <Video className="w-4 h-4" data-id="element-77" />
              Attach a video to this article
            </button>}
          {showVideoSelector && !attachedVideo && <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100" data-id="element-78">
              {videos.map(video => <button key={video.id} type="button" onClick={() => {
            setAttachedVideoId(video.id);
            setShowVideoSelector(false);
          }} className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 text-left" data-id="element-79">
                  <img src={video.thumbnailUrl} alt="" className="w-12 h-8 object-cover rounded" data-id="element-80" />
                  <div className="flex-1 min-w-0" data-id="element-81">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-82">
                      {video.title}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-83">{video.duration}</p>
                  </div>
                </button>)}
            </div>}
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-84">
          <Button variant="secondary" onClick={onClose} type="button" data-id="element-85">
            Cancel
          </Button>
          <Button variant="primary" type="submit" data-id="element-86">
            Publish Article
          </Button>
        </div>
      </form>
    </Modal>;
}